import {
  GetServerSideProps,
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  type Redirect,
} from 'next'
import { safeGetEducatorProfile } from '@educator/api-hooks/profile/safeGetEducatorProfile'
import {
  tryCatch,
  EducatorEnrollmentService,
  CoreOpenAPI,
} from '@educator/libs/coreApi'
import type { EducatorProfileResponse } from '@mc/educator-api'
import { reportError } from '@mc/next-newrelic'
import { getApiErrorStatus } from './apiErrors'
import { getUser } from './getUser'
import { paths } from './paths'
import { getOrRefreshAccessToken } from './tokenRefresh'

export interface UserProfile {
  sub: string
  name?: string
  email?: string
  picture?: string
  nickname?: string
  updated_at?: string
}

export interface AuthProps {
  user: UserProfile | null
  accessToken: string
}

export interface LearnerAuthProps extends AuthProps {
  /** Absent when the profile fetch failed and the onboarding check failed open. */
  profile?: EducatorProfileResponse
}

/**
 * Get the user session server-side in getServerSideProps
 */
export const getServerSession = async (ctx: GetServerSidePropsContext) => {
  try {
    // Try to get or refresh access token
    const accessToken = await getOrRefreshAccessToken({
      req: ctx.req,
      res: ctx.res,
    })

    if (!accessToken) {
      return null
    }

    // Get user info from backend API
    const user = await getUser(accessToken)

    if (!user) {
      return null
    }

    return { user, accessToken }
  } catch (error) {
    return null
  }
}

/**
 * Get user from session
 */
export const getUserFromSession = (
  session: Awaited<ReturnType<typeof getServerSession>>,
) => {
  if (!session?.user) {
    return null
  }
  return session.user as UserProfile
}

/**
 * Protect a page route - redirects to login if not authenticated
 * Returns user profile if authenticated
 *
 * Learner pages use `requireAuthWithProgramOnboarding` instead; plain
 * `requireAuth` is for pages a learner must reach before finishing onboarding
 * (enrollment, account, onboarding itself).
 */
export const requireAuth = async (ctx: GetServerSidePropsContext) => {
  const session = await getServerSession(ctx)

  if (!session) {
    return {
      redirect: {
        destination: `${paths.login}?returnTo=${encodeURIComponent(ctx.resolvedUrl)}`,
        permanent: false,
      },
    }
  }

  const user = getUserFromSession(session)

  return {
    props: {
      user,
      accessToken: session.accessToken,
    },
  }
}

// Values of `EducatorApplicationStatus`, which the endpoint derives via
// `display_status` — the raw `complete` column arrives here as `completed`.
const ENTITLED_STATUSES = ['completed']

/**
 * Redirect mapping for users who are not yet entitled.
 * Maps enrollment status to the appropriate page in the enrollment flow.
 */
const getEntitlementRedirect = (status: string): string => {
  switch (status) {
    case 'draft':
      return paths.enrollmentOverview
    case 'published':
    case 'pending_partner_sync':
    case 'pending_challenge_completion':
    case 'pending_final_decision':
      return paths.enrollmentStatus
    case 'rejected':
      return paths.enrollmentRejected
    case 'pending_payment':
      return paths.enrollmentCheckout
    default:
      return paths.enrollmentOverview
  }
}

/**
 * The viewer's enrollment status. A missing application (404) and a failed
 * lookup both read as 'draft', the least-entitled status.
 */
const getEnrollmentStatus = async (accessToken: string): Promise<string> => {
  CoreOpenAPI.TOKEN = accessToken

  const response = await tryCatch(() =>
    EducatorEnrollmentService.getEducatorEnrollmentApplication(),
  )

  if ('data' in response && response.data) {
    return response.data.status || 'draft'
  }
  return 'draft'
}

/**
 * `unknown` is load-bearing: the lookup failed, which is not "no access".
 * Collapsing the two locks a paying member out over a transient blip.
 */
export type EducatorEntitlement = 'entitled' | 'not_entitled' | 'unknown'

/**
 * Whether the session may reach member-only surfaces — the same status check
 * `requireAuthWithEntitlement` gates on, for callers that need the answer
 * without its enrollment-flow redirects. A public page uses it to decide
 * whether to hand a visitor over to the in-portal version of itself.
 */
export const getEducatorEntitlement = async (
  accessToken: string,
): Promise<EducatorEntitlement> => {
  CoreOpenAPI.TOKEN = accessToken

  const response = await tryCatch(() =>
    EducatorEnrollmentService.getEducatorEnrollmentApplication(),
  )

  if ('data' in response && response.data) {
    return ENTITLED_STATUSES.includes(response.data.status || 'draft')
      ? 'entitled'
      : 'not_entitled'
  }

  const error = 'error' in response ? response.error : undefined
  // No application is a real answer; anything else is the lookup failing.
  if (getApiErrorStatus(error) === 404) return 'not_entitled'

  reportError(error, { location: 'getEducatorEntitlement' })
  return 'unknown'
}

/**
 * Protect a page route with authentication and enrollment entitlement check.
 * - Redirects to login if not authenticated
 * - Redirects to the appropriate enrollment page if the user has not completed enrollment
 * - Only users with 'complete' or 'pending_installment_payments' status can access entitled pages
 *
 * Use this for pages that require a fully enrolled user (e.g., learning modules, settings).
 */
export const requireAuthWithEntitlement = async (
  ctx: GetServerSidePropsContext,
) => {
  const authResult = await requireAuth(ctx)

  if ('redirect' in authResult) {
    return authResult
  }

  try {
    const effectiveStatus = await getEnrollmentStatus(
      authResult.props.accessToken,
    )

    if (!ENTITLED_STATUSES.includes(effectiveStatus)) {
      return {
        redirect: {
          destination: getEntitlementRedirect(effectiveStatus),
          permanent: false,
        },
      }
    }
  } catch {
    // Fail open: if enrollment API fails, allow access (don't lock out users on API errors)
  }

  return authResult
}

/**
 * Sends a learner who hasn't finished program onboarding back into the funnel
 * (root → program details → onboarding). Fails open: when the profile can't be
 * fetched the learner is let through, with no profile.
 *
 * @param accessToken The learner's access token, used to fetch the profile.
 * @param sourcePage The page being guarded, reported if the profile fetch fails.
 */
export const requireProgramOnboarding = async (
  accessToken: string,
  sourcePage?: string,
): Promise<{ redirect: Redirect } | { profile?: EducatorProfileResponse }> => {
  const profile = await safeGetEducatorProfile(accessToken, sourcePage)

  if (profile && !profile.isOnboarded) {
    return { redirect: { destination: paths.root, permanent: false } }
  }

  return { profile }
}

/**
 * Protect a learner page: `requireAuth`, then `requireProgramOnboarding`.
 * Returns the profile it fetched alongside the auth props, so the page doesn't
 * fetch it again.
 */
export const requireAuthWithProgramOnboarding = async (
  ctx: GetServerSidePropsContext,
): Promise<{ redirect: Redirect } | { props: LearnerAuthProps }> => {
  const authResult = await requireAuth(ctx)

  if ('redirect' in authResult) return authResult

  const onboardingResult = await requireProgramOnboarding(
    authResult.props.accessToken,
    ctx.resolvedUrl,
  )

  if ('redirect' in onboardingResult) return onboardingResult

  return {
    props: { ...authResult.props, profile: onboardingResult.profile },
  }
}

const withAuthentication =
  <A extends AuthProps>(
    authenticate: (
      ctx: GetServerSidePropsContext,
    ) => Promise<{ redirect: Redirect } | { props: A }>,
  ) =>
  <P extends object = object>(
    callback: (
      ctx: GetServerSidePropsContext,
      authProps: A,
    ) => Promise<GetServerSidePropsResult<P>>,
  ): GetServerSideProps<P> =>
  async (ctx) => {
    const authResult = await authenticate(ctx)

    // If not authenticated, return redirect
    if ('redirect' in authResult) {
      return authResult as GetServerSidePropsResult<P>
    }

    // Call the callback with context and auth props
    const callbackResult = await callback(ctx, authResult.props)

    // Merge props
    if ('props' in callbackResult) {
      const resolvedProps = await Promise.resolve(callbackResult.props)
      return {
        props: {
          ...resolvedProps,
          ...(authResult.props.user ? { user: authResult.props.user } : {}),
        } as P & { email?: string },
      }
    }

    // If callback returns redirect or notFound, pass through
    return callbackResult as GetServerSidePropsResult<P>
  }

/**
 * Higher-order function that wraps getServerSideProps with authentication
 * - Redirects to login if not authenticated
 * - Calls the provided callback if authenticated
 */
export const getServerSidePropsWithAuthRequired =
  withAuthentication(requireAuth)

/**
 * `getServerSidePropsWithAuthRequired` for learner pages: authenticates with
 * `requireAuthWithProgramOnboarding`, so the callback also receives the profile.
 */
export const getServerSidePropsWithProgramOnboardingRequired =
  withAuthentication(requireAuthWithProgramOnboarding)

export type RegistrationMethod = 'google' | 'facebook' | 'apple' | 'email'

export const mapCurrentAuthTypeToRegistrationMethod = (
  currentAuthType: string | undefined,
): RegistrationMethod | undefined => {
  if (!currentAuthType) return undefined

  switch (currentAuthType.toLowerCase()) {
    case 'google_oauth2':
      return 'google'
    case 'facebook':
      return 'facebook'
    case 'apple':
      return 'apple'
    case 'identity':
    case 'email':
      return 'email'
    default:
      return undefined
  }
}
